import React from 'react';

import type { MatchingBoardItemDto, MatchingLevel, MatchingMetaDto } from '../../api/matchingTypes';
import type { Role } from '../../api/types';
import { formatClubWindow, groupPreferences } from '../../domain/matching/matching';
import { useAppSelector } from '../../store/hooks';
import { selectAllGames } from '../../store/selectors/gamesSelectors';
import { TavernButton, TavernCard, TavernCardTime } from '../../ui/components';
import { GameCard } from '../games/GameCard';
import { RoleBadge } from '../members/RoleBadge';

import '../events/EventCard.css';
import '../members/MemberItem.css';
import './matching.css';

type Props = {
  item: MatchingBoardItemDto;
  meta: MatchingMetaDto | null;
  /** The viewer already has a current request on this date ("Искать вместе" is disabled). */
  alreadyHasRequest: boolean;
  onEdit: (item: MatchingBoardItemDto) => void;
  onDelete: (item: MatchingBoardItemDto) => void;
  onCopy: (item: MatchingBoardItemDto) => void;
};

function toRole(role: string): Role {
  const value = role.toLowerCase();
  if (value.includes('admin')) return 'admin';
  if (value.includes('organizer')) return 'organizer';
  return 'user';
}

const PREF_GROUPS: { level: MatchingLevel; title: string; hint?: string }[] = [
  { level: 'WANT', title: 'Хочет', hint: 'любую из списка' },
  { level: 'ACCEPTABLE', title: 'Будет' },
  { level: 'EXCLUDE', title: 'Не хочет' },
];

export const MatchingRequestCard = React.memo(function MatchingRequestCard({
  item,
  meta,
  alreadyHasRequest,
  onEdit,
  onDelete,
  onCopy,
}: Props) {
  const author = item.author;
  const initial = author?.displayName?.[0]?.toUpperCase() ?? '?';
  const photo = author?.photoUrl;
  const hasPhoto = !!photo && (photo.startsWith('http') || photo.startsWith('/'));

  const nameOf = (type: 'GAME' | 'TAG', id: number) =>
    type === 'GAME'
      ? (meta?.games.find((g) => g.id === id)?.name ?? `Игра ${id}`)
      : (meta?.tags.find((t) => t.id === id)?.name ?? String(id));

  const grouped = groupPreferences(item.preferences);
  // All preference groups start collapsed.
  const [expanded, setExpanded] = React.useState<Set<MatchingLevel>>(() => new Set());
  const toggle = (level: MatchingLevel) =>
    setExpanded((prev) => {
      const next = new Set(prev);
      if (!next.delete(level)) next.add(level);
      return next;
    });
  const catalog = useAppSelector(selectAllGames);
  const gameById = React.useMemo(() => new Map(catalog.map((g) => [g.id, g])), [catalog]);

  return (
    <TavernCard>
      <div className="matching-card">
        <div className="matching-card__top">
          <div className="member-item matching-card__author">
            <div className="mi-avatar-wrap">
              <div className="mi-avatar">
                {hasPhoto ? (
                  <img src={photo ?? undefined} alt="" />
                ) : (
                  <span className="member-avatar-placeholder">{initial}</span>
                )}
              </div>
            </div>
            {author ? (
              <RoleBadge role={toRole(author.role)} hasClubKey={author.hasClubKey} />
            ) : null}
            <div className="mi-content">
              <span className="member-name">
                {author?.username ? (
                  <a className="matching-card__link" href={`https://t.me/${author.username}`}>
                    {author.displayName}
                  </a>
                ) : (
                  (author?.displayName ?? 'Участник')
                )}
              </span>
            </div>
          </div>
        </div>

        <div className="matching-card__when">
          <TavernCardTime timeRange={`Начало: ${formatClubWindow(item.windowStart, item.windowEnd)}`} />
          {item.own ? (
            <div className="event-card-actions">
              <button
                type="button"
                className="event-edit-btn"
                onClick={() => onEdit(item)}
                aria-label="Редактировать"
                title="Редактировать"
              >
                ✎
              </button>
              <button
                type="button"
                className="event-delete-btn"
                onClick={() => onDelete(item)}
                aria-label="Удалить"
                title="Удалить"
              >
                🗑
              </button>
            </div>
          ) : null}
        </div>

        {item.preferences.length > 0 ? (
          <div className="matching-card__prefs">
            {PREF_GROUPS.filter((g) => grouped[g.level].length > 0).map((g) => {
              const tags = grouped[g.level].filter((p) => p.targetType === 'TAG');
              const games = grouped[g.level].filter((p) => p.targetType === 'GAME');
              const open = expanded.has(g.level);
              return (
                <div
                  key={g.level}
                  className={`matching-card__pref-group matching-card__pref-group--${g.level.toLowerCase()}`}
                >
                  <button
                    type="button"
                    className="matching-card__pref-title matching-card__pref-toggle"
                    aria-expanded={open}
                    onClick={() => toggle(g.level)}
                  >
                    <span>
                      {g.title}
                      <span className="matching-card__pref-count">{grouped[g.level].length}</span>
                      {g.hint ? <span className="matching-card__pref-hint"> · {g.hint}</span> : null}
                    </span>
                    <span className="matching-card__pref-arrow" aria-hidden="true">
                      {open ? '▲' : '▼'}
                    </span>
                  </button>

                  {open && tags.length > 0 ? (
                    <div className="matching-card__pref-section">
                      <div className="matching-card__pref-caption">Теги</div>
                      <div className="matching-card__pref-items">
                        {tags.map((p) => (
                          <span
                            key={p.targetId}
                            className="matching-card__pref-chip matching-card__pref-chip--tag"
                          >
                            {nameOf('TAG', p.targetId)}
                          </span>
                        ))}
                      </div>
                    </div>
                  ) : null}

                  {open && games.length > 0 ? (
                    <div className="matching-card__pref-section">
                      <div className="matching-card__pref-caption">Игры</div>
                      <div className="matching-card__pref-games">
                        {games.map((p) => {
                          const game = gameById.get(String(p.targetId));
                          return game ? (
                            <GameCard key={p.targetId} game={game} />
                          ) : (
                            <span key={p.targetId} className="matching-card__pref-chip">
                              {nameOf('GAME', p.targetId)}
                            </span>
                          );
                        })}
                      </div>
                    </div>
                  ) : null}
                </div>
              );
            })}
          </div>
        ) : (
          <div className="matching-card__meta">Подойдёт любая игра</div>
        )}

        {item.own ? null : (
          <div className="matching-card__actions">
            <TavernButton
              variant="primary"
              disabled={alreadyHasRequest}
              onClick={() => onCopy(item)}
            >
              Искать вместе
            </TavernButton>
            {alreadyHasRequest ? (
              <span className="matching-card__hint">У вас уже есть заявка</span>
            ) : null}
          </div>
        )}
      </div>
    </TavernCard>
  );
});
